import { red } from '@mui/material/colors';
import React, { useContext, useState } from 'react';
import { get_cod } from '../../../utils/cod';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import { showDeliveryState } from '../../common/styles/order-states.style';
import {
  getPickupTaskStateColor,
  getPickupTaskStateIcon,
  showPickupTaskChip,
  showPickupTaskState,
} from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { format } from 'date-fns';
import { Box, IconButton } from '@mui/material';
import { Workflow } from '../../../enums/orders';
import {
  canCompletePickupTask,
  canCancelPickupTask,
} from '../../../utils/pickup-tasks';
import { AuthContext } from '../../../contexts/user-context/user-context';
import HouseIcon from '@mui/icons-material/House';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';
import { ClearIcon } from '@mui/x-date-pickers';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import CancelIcon from '@mui/icons-material/Cancel';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { DriverWithIcon } from '../../common/styles/driver-with-icon';
import { FactCheckOutlined } from '@mui/icons-material';
import StaticGoogleMap from '../../common/static-google-map/static-google-map';
import { CustomerType } from '../../../interfaces/common/customer';

interface ResponsivePickupTasksListingProps {
  tasksList: IPickupTask[];
  isLoading: boolean;
  setCancellingPickupRequest: React.Dispatch<
    React.SetStateAction<IPickupTask | undefined>
  >;
  setDownloadWaybillForTask: (task: IPickupTask) => void;
  setEditingTask: React.Dispatch<React.SetStateAction<IPickupTask | null>>;
}

const ResponsivePickupTasksListing = ({
  tasksList,
  isLoading,
  setCancellingPickupRequest,
  setDownloadWaybillForTask,
  setEditingTask,
}: ResponsivePickupTasksListingProps) => {
  const { user, config } = useContext(AuthContext);
  const [editingCustomer, setEditingCustomer] = useState<CustomerType | null>(
    null
  );

  return (
    <>
      <GenericList<IPickupTask>
        sourceView="pickup-tasks-listing"
        items={tasksList}
        selectedItems={new Map()}
        updateSelectedItems={() => {
          throw new Error('unimplemented');
        }}
        isLoading={isLoading}
        fullWidth
        columns={[
          {
            id: 'task_id',
            header: <p>Task ID</p>,
            display_body: (task) => (
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => setEditingTask(task)}
              >
                <b>
                  <u>{task.id}</u>
                </b>
              </div>
            ),
          },
          {
            id: 'creation_date',
            header: <p>Created At</p>,
            display_body: (task) => (
              <p>
                {format(new Date(task.creation_date ?? ''), 'dd-MM-yyyy')}

                <br />
                {format(new Date(task.creation_date ?? ''), 'HH:mm:ss')}
              </p>
            ),
          },
          {
            id: 'workflow',
            header: <p>Workflow</p>,
            display_body: (task) => showWorkflow(task.workflow, false, undefined, ""),
          },
          {
            id: 'Pickup From',
            header: <p>Pickup From</p>,
            display_body: (task) => {
              if (task.workflow === Workflow.STANDARD)
                return (
                  <>
                    <StorefrontIcon color="primary" /> <br />{' '}
                      <b>{task.merchant?.name}</b>
                      <br />
                      {displayLocation(task.merchant_location)}
                  </>
                );
              else
                return (
                  <>
                    {' '}
                    <HouseIcon color="primary" /> <br />
                    <div
                      onClick={() => setEditingCustomer(task.customer)}
                      style={{ cursor: 'pointer' }}
                    >
                      {task.customer?.name ?? 'No Name'}
                    </div>
                    {displayLocation(task.customer_location)}{' '}
                  </>
                );
            },
          },
          {
            id: 'task_state',
            header: <p>Status</p>,
            display_body: (task) => (
              <>{showPickupTaskState(task.status, task.driver)}</>
            ),
          },
          {
            id: 'package count',
            header: <p># Packages</p>,
            display_body: (task) => <p>{task.package_count}</p>,
          },
          {
            id: 'download_waybills',
            header: <p>Waybills</p>,
            display_body: (task) => {
              return (
                <IconButton onClick={() => setDownloadWaybillForTask(task)}>
                  <DownloadIcon fontSize="large" color="primary" />
                </IconButton>
              );
            },
          },
          {
            id: 'cancel_request',
            header: <p>Cancel</p>,
            display_body: (task) => {
              return (
                <IconButton
                  disabled={!canCancelPickupTask(task, user)}
                  onClick={() => setCancellingPickupRequest(task)}
                >
                  <CancelIcon fontSize="large" />
                </IconButton>
              );
            },
          },
        ]}
        getItemUID={(order) => order.id}
        rowConfigs={[
          {
            display_row: (task) => <b>Pickup #{task.id}</b>,
          },
          {
            display_row: (task) => (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0', // Light gray background
                  borderRadius: '12px', // Rounded corners
                  padding: '4px 8px', // Padding around the text
                }}
              >
                {task.package_count} items
              </Box>
            ),
          },
          {
            display_row: (task) => (
              <>
                {task.creation_date &&
                  new Date(task.creation_date).toLocaleDateString()}
              </>
            ),
          },
          {
            display_row: (task) => {
              const icon =
                task.workflow === Workflow.STANDARD ? (
                  <StorefrontIcon color="primary" />
                ) : (
                  <PersonOutlineIcon color="primary" />
                );
              return (
                <IconWithText>
                  {icon}
                  <>
                    {task.workflow === Workflow.STANDARD ? (
                      <div>
                        <b>
                          {user?.name} {task.merchant_location?.contact_phone}
                        </b>
                      </div>
                    ) : (
                      <div>
                        <b>
                          {task.customer?.name} {task.customer?.phone_number}
                        </b>
                      </div>
                    )}
                  </>
                </IconWithText>
              );
            },
          },
          {
            display_row: (task) => {
              return (
                <IconWithText>
                  <LocationOnIcon color="primary" />
                  <>
                    {task.workflow === Workflow.STANDARD ? (
                      <b>{displayLocation(task.merchant_location)}</b>
                    ) : (
                      <b>{displayLocation(task.customer_location)}</b>
                    )}
                  </>
                </IconWithText>
              );
            },
          },
          {
            display_row: (task) => {
              return mobileDoubleItemsGrid([
                <DriverWithIcon key={'driver'} driver={task.driver} />,
                showWorkflow(task.workflow, false, undefined, ""),
              ]);
            },
          },
          {
            display_row: (task) => {
              const mapPath = extractMapsPlaceLink(
                task.workflow == Workflow.STANDARD
                  ? task.merchant_location
                  : task.customer_location
              );
              const url =
                task.workflow == Workflow.STANDARD
                  ? task.merchant_location?.url
                  : task.customer_location?.url;
              return <StaticGoogleMap mapPath={mapPath} url={url} />;
            },
          },
          {
            display_row: (task) => {
              return mobileDoubleItemsGrid([
                <div
                  key={'download waybills'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                  }}
                >
                  Cancel Pickup <br />
                  <IconButton
                    disabled={!canCancelPickupTask(task, user)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCancellingPickupRequest(task);
                    }}
                  >
                    <CancelIcon fontSize="large" />
                  </IconButton>
                </div>,
                <div
                  key={'download waybills'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'center',
                  }}
                >
                  Waybills <br />
                  <IconButton
                    key={'download waybills'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDownloadWaybillForTask(task);
                    }}
                  >
                    <DownloadIcon fontSize="large" color="primary" />
                  </IconButton>
                </div>,
              ]);
            },
          },
        ]}
        cardFooter={(task) => {
          return {
            content: (
              <IconWithText centered>
                {getPickupTaskStateIcon(task.status)}
                <>{task.status.replaceAll('_', ' ')}</>
              </IconWithText>
            ),
            background: getPickupTaskStateColor(task.status),
          };
        }}
        cardIcon={(task) => <FactCheckIcon color={'primary'} />}
        onCardClick={(task) => setEditingTask(task)}
      />
    </>
  );
};

export default ResponsivePickupTasksListing;
